import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';

import { animated, useSpring } from '@react-spring/web';
import { useDataStore } from 'files/StateManagement';

import { AltBadge } from 'views/Main.js';
import Appearance from 'styles/Appearance.js';
import { BorderBeam } from 'views/MagicUI.js';
import Button from 'views/Button.js';
import Draggable from 'react-draggable';
import ProgressBar from 'views/ProgressBar.js';
import ResizeObserver from 'react-resize-observer';
import ResourceProgressView from 'views/ResourceProgressView.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';
import { VideoPlayer } from 'managers/Components.js';

export const CalloutIndex = 4950;
export const FloatingLayerMenuIndex = 7500;
export const FloatingMenuIndex = 4550;
export const StartIndex = 4990;
//export const FrontIndex = StartIndex + 10;
export const LayerItemSpacing = 36;

export const getLayerSizingWidth = sizing => {
    if(Utils.isMobile() === true) {
        return window.innerWidth - 12;
    }
    switch(sizing) {
        case 'small':
        return 400;

        case 'medium':
        return 550;

        case 'extra_large':
        return window.innerWidth < 1250 ? window.innerWidth : 1250;

        case 'fullscreen':
        return window.innerWidth - 48;

        default:
        return 700;
    }
}

const Layer = React.forwardRef(({ buttons, children, id, options = {}, utils }, forwardedRef) => {

    const { layerState, onCloseButtonClick, onRequestClose } = options;

    const buttonContainer = useRef(null);
    const headerRef = useRef(null);
    const ref = useRef(null);
    const scrollContainer = useRef(null);
    const stickyBottom = useRef(null);
    const stickyTop = useRef(null);

    const [animations, animationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        transform: 'scale(0.75)'
    }));
    const [dragging, setDragging] = useState(false);
    const [expandedWarnings, setExpandedWarnings] = useState({});
    const [height, setHeight] = useState(0);
    const [layerPosition, setLayerPosition] = useState(options.position);
    const [layerStatePrivate, setLayerStatePrivate] = useState(null);
    const { setRequiem } = useDataStore();
    const [size, setSize] = useState({
        height: window.innerHeight,
        type: Utils.isMobile() === true ? 'fullscreen' : (options.sizing || 'medium'),
        width: window.innerWidth
    });
    const [zIndex, setZIndex] = useState(options.zIndex);

    const onAnimateComponentsIntoView = () => {

        animationsApi.start({ opacity: 1, transform: 'scale(1)' })
        return;
        
        // Calculate opacity based on layer index and total layer count
        // Higher index = closer to top (forefront layer is always 100%)
        // Only show top 4 layers, with opacity decreasing by 25% for each layer down
        const getOpacityForIndex = (index, totalLayers) => {
            if (index === undefined || index === null) return 1;
            
            // Single layer is always 100%
            if (totalLayers === undefined || totalLayers <= 1) return 1;
            
            // Calculate position from top (0 = topmost layer)
            const positionFromTop = (totalLayers - 1) - index;
            
            console.log(`Layer index: ${index}, totalLayers: ${totalLayers}, positionFromTop: ${positionFromTop}`);
            
            // Layers beyond the 4th from top are hidden
            if (positionFromTop > 3) return 0;
            
            // Top layer = 100%, each layer down decreases by 25%
            // positionFromTop 0: 100%, 1: 75%, 2: 50%, 3: 25%
            const calculatedOpacity = 1 - (positionFromTop * 0.25);
            console.log(`Calculated opacity: ${calculatedOpacity}`);
            return calculatedOpacity;
        };
        
        const opacity = getOpacityForIndex(options.index, options.totalLayersCount);
        animationsApi.start({ opacity, transform: 'scale(1)' })
    }

    const onDragEnded = (_, node) => {

        // update layer position
        setDragging(false);
        setLayerPosition({ x: node.x, y: node.y });

        // notify subscribers of reposition if applicable
        if(typeof(options.onReposition) === 'function') {
            options.onReposition({
                id: id,
                position: {
                    x: node.x,
                    y: node.y
                }
            });
        }
    }

    const onDragMoved = () => {
        setDragging(true);
    }

    const onDragStarted = () => {
        // trigger a layer press when drag has started in order to bring layer to the foreground
        onLayerPress();
    }

    const onLayerAction = ({ detail }) => {
        if(detail.layerID === id) {
            onLayerStateChange(detail.action);
        }
    }

    const onLayerPress = () => {
        //setZIndex(FrontIndex);
        //options.onSetLayerIndex(id);
    }

    const onMouseEnter = () => {
        if(Utils.isMobile() === false) {
            document.body.style.overflowY = 'hidden';
        }
    }

    const onMouseLeave = () => {
        if(Utils.isMobile() === false) {
            document.body.style.overflowY = 'scroll';
        }
    }

    const onUpdateHeight = rect => {

        // update height state
        setHeight(rect.height);
        
        // notify subscribers of height change if applicable
        if(typeof(options.onUpdateHeight) === 'function') {
            options.onUpdateHeight(rect.height)
        }
    }

    const onLayerStateChange = async state => {
        try {

            // determine if a close action was received
            if(state === 'close') {

                // determine if a pre-close confirmation needs to be requested
                if(shouldRequestClose() === true) {
                    await utils.alert.showAsync({
                        title: `Close Layer`,
                        message: 'Are you sure that you want to close this layer? All unsaved changes will be lost.',
                        buttons: [{
                            key: 'confirm',
                            title: 'Yes',
                            style: 'destructive'
                        },{
                            key: 'cancel',
                            title: 'Do Not Close',
                            style: 'default'
                        }],
                        onClick: (key, resolve, reject) => {
                            if(key === 'confirm') {
                                resolve();
                                return;
                            }
                            let error = new Error('user rejected close');
                            reject(error);
                        }
                    });
                }

                // update animation values for layer
                animationsApi.start({ opacity: 0, transform: 'scale(0.75)' });

                // re-enable body scrolling 
                //document.body.style.overflowY = 'scroll';

                // notify subscribers of layer close if applicable
                if(typeof(options.onClose) === 'function') {
                    setTimeout(options.onClose.bind(this, id), 500);
                }
                return;
            }

            // update internal layer state for other state values
            setLayerStatePrivate(state);

        } catch(e) {
            console.error(e.message);
        }
    }

    const onRequiemSupportClick = props => {
        setRequiem(requiem => {
            requiem.request = {
                action: 'smart_flow_init', //'smart_flow_start',
                smart_flow: props.smart_flow
            };
            return requiem;
        })
    }

    const onScrollPositionChange = () => {
        
        // Use tolerance of 1px to account for subpixel rendering on mobile devices
        let { scrollHeight, scrollTop, clientHeight } = scrollContainer.current;
        if(Math.abs(scrollHeight - scrollTop - clientHeight) < 1) {
            options.onScrollToBottom();
        }
    }

    const onSupportVideoClick = url => {
        utils.layer.open({
            Component: VideoPlayer.bind(this, { url }),
            id: 'video_player'
        });
    }

    const onWindowSizeChange = () => {
        setSize(props => ({
            ...props,
            height: window.innerHeight,
            width: window.innerWidth
        }))
    }

    const getButtons = () => {

        // no additional logic is required if layer state is submit_api_request
        if(layerStatePrivate === 'submit_api_request') {
            return null;
        }

        // prepare list of target buttons 
        let targets = buttons?.filter(button => button.visible !== false) || [];

        // reverse order for mobile devices
        if(Utils.isMobile() === true) {
            targets = targets.reverse();
        }

        // return buttons if at least one eligible button was found
        return targets.length > 0 && (
            <div
            className={Utils.isMobile() === true ? 'safe-area-bottom-padding' : ''}
            ref={buttonContainer}
            style={{
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                paddingLeft: 12,
                paddingRight: 12,
                width: '100%'
            }}>
                <div className={'row p-0 m-0'}>
                    {targets.map((button, index, buttons) => {
                        return (
                            <div
                            key={index}
                            className={`col-12 col-md-${parseInt(12 / buttons.length)} pt-${index === 0 ? 3 : 2} pb-${index === targets.length - 1 ? 3 : 0} px-1 py-md-3 px-md-1`}>
                                <Button
                                {...button}
                                key={null}
                                label={button.text}
                                type={'large'}/>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    const getChildren = () => {

        // return api request components if layer state is submit_api_request
        if(layerStatePrivate === 'submit_api_request') {
            let { message, request, title } = options.apiRequest || {};
            return (
                <ResourceProgressView
                message={message}
                request={request}
                title={title} />
            );
        }
        return children;
    }

    const getFilterOptions = () => {

        // no additional logic is required if layer state is submit_api_request
        if(layerStatePrivate === 'submit_api_request') {
            return null;
        }
        
        if(!options.filter) {
            return null;
        }
        return (
            <div
            className={'not-draggable'}
            style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 15
            }}>
                <TextField
                icon={'search'}
                placeholder={options.filter.placeholder || 'Search for something...'}
                onChange={options.filter.onChange} />
            </div>
        )
    }

    const getLayerHeight = () => {

        let height = size.height;
        if(buttonContainer.current) {
            height -= buttonContainer.current.clientHeight;
        }
        if(stickyTop.current) {
            height -= stickyTop.current.clientHeight;
        }
        if(headerRef.current) {
            height -= headerRef.current.clientHeight;
        }
        if(stickyBottom.current) {
            height -= stickyBottom.current.clientHeight;
        }
        
        if(Utils.isMobile() === true) {
            return height;
        }
        return height - (size.type === 'fullscreen' ? 20 : 60);
    }

    const getLayerPosition = () => {
        if(size.type === 'fullscreen') {
            return {
                x: 0,
                y: 0
            }
        }
        return layerPosition || {
            x: (size.width / 2) - (getLayerWidth() / 2),
            y: height ? ((size.height / 2) - (height / 2)) : 50
        }
    }

    const getLayerWidth = () => {
        if(size.type === 'fullscreen') {
            return window.innerWidth;
        }
        let width = getWidthForSizing();
        return size.width > width ? width : size.width - 60;
    };

    const getSupportComponents = () => {

        let { description, enabled = true, requiem, title, videoUrl } = options.support || { enabled: false };
        let hasSupportVideo = typeof(videoUrl) === 'string';
        let requiemEnabled = requiem?.enabled === true;

        // no additional logic is required if layer state is submit_api_request
        if(layerStatePrivate === 'submit_api_request') {
            return null;
        }

        return enabled === true && (
            <LayerItem
            collapsed={false}
            title={title || 'Get Started'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    overflow: 'visible',
                    padding: 16,
                    paddingTop: 12,
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        marginBottom: 24
                    }}>{description}</span>
                    {(hasSupportVideo === true || requiemEnabled === true) && (
                        <div 
                        className={'row m-0'}
                        style={{
                            width: '100%'
                        }}>
                            {hasSupportVideo === true && (
                                <div 
                                className={`col-12 col-lg-${requiemEnabled ? 6 : 12} px-0 py-0 pr-lg-${requiemEnabled ? 2 : 0} pl-lg-0`}
                                style={{
                                    width: '100%'
                                }}>
                                    <Button 
                                    color={Appearance.colors.secondary()}
                                    icon={'images/play-button-white.png'}
                                    label={'Watch Tutorial'}
                                    onClick={onSupportVideoClick.bind(this, videoUrl)}
                                    type={'large'} /> 
                                </div>
                            )}
                            {requiemEnabled === true && (
                                <div 
                                className={`col-12 col-lg-${hasSupportVideo ? 6 : 12} px-0 pt-0 mt-${hasSupportVideo ? 2 : 0} pb-0 pt-lg-0 pr-lg-0 pl-lg-${hasSupportVideo ? 2 : 0} mt-lg-0 text-button`}
                                onClick={onRequiemSupportClick.bind(this, requiem)}
                                style={{
                                    alignItems: 'center',
                                    backgroundColor: Appearance.colors.divider(),
                                    borderRadius: 8,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    height: 45,
                                    minWidth: 0,
                                    padding: '6px 12px 6px 12px',
                                    position: 'relative',
                                    whiteSpace: 'normal',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        color: Appearance.colors.text(),
                                        display: 'block',
                                        fontSize: 12,
                                        fontFamily: 'Zen Dots',
                                        fontWeight: 800,
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{'Ask Requiem'.toUpperCase()}</span>
                                    <BorderBeam
                                    colorFrom={Appearance.colors.yellow}
                                    colorTo={Appearance.colors.red}
                                    duration={8}
                                    useSize={true}
                                    style={{
                                        border: `2px solid ${Appearance.colors.softBorder()}`,
                                        borderRadius: 8 
                                    }} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </LayerItem>
        )
    }

    const getWarnings = () => {

        let warnings = Object.values(options.warnings || {}).filter(value => value ? true : false);
        return warnings.length > 0 && (
            <div style={{
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
                padding: 16,
                width: '100%'
            }}>
                {warnings.map((entry, index) => {
                    
                    const isExpanded = expandedWarnings[index];
                    const isMobile = Utils.isMobile() === true;
                    
                    const onToggleContentExpand = () => {
                        setExpandedWarnings(prev => ({
                            ...prev,
                            [index]: !prev[index]
                        }));
                    }

                    const onWarningClick = () => {
                        if(isMobile === false || isExpanded === true) {
                            if(typeof(entry.onClick) === 'function') {
                                entry.onClick();
                            }
                            return;
                        }
                        onToggleContentExpand();
                    }

                    return (
                        <div 
                        className={entry.onClick ? 'text-button' : ''}
                        key={index}
                        onClick={onWarningClick}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            alignItems: 'center',
                            backgroundColor: Appearance.colors.panelBackground(),
                            borderRadius: 16,
                            borderStyle: 'solid',
                            borderWidth: 2,
                            backgroundColor: Appearance.colors.yellow,
                            display: 'flex',
                            flexDirection: 'row',
                            marginBottom: index !== warnings.length - 1 ? 8 : 0,
                            overflow: 'hidden',
                            padding: '12px 16px 12px 16px',
                            width: '100%'
                        }}>
                            <img 
                            src={'images/warning-icon-white.png'}
                            style={{
                                height: 35,
                                marginRight: 12,
                                width: 35
                            }} />
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                color: 'white',
                                flexGrow: 1,
                                ...isMobile && !isExpanded && {
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                }
                            }}>{entry.description}</span>
                            {isMobile === true && (
                                <CollapseArrow 
                                collapsed={isExpanded ? false : true} 
                                color={'white'}
                                onClick={(val, evt) => {
                                    evt.stopPropagation();
                                    onToggleContentExpand();
                                }}
                                style={{
                                    marginLeft: 12 
                                }} />
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    const getWidthForSizing = () => {
        return getLayerSizingWidth(size.type);
    }

    const shouldRequestClose = () => {
        return typeof(onRequestClose) === 'function' && onRequestClose() === true;
    }

    const shouldShowScrollbars = () => {
        if(Utils.isMobile() === true) {
            return false;
        }
        return scrollContainer.current ? scrollContainer.current.scrollHeight > getLayerHeight() : false;
    }

    useEffect(() => {
        if(height && ref.current) {
            setLayerPosition({
                x: (size.width / 2) - (ref.current.clientWidth / 2),
                y: (size.height / 2) - (ref.current.clientHeight / 2)
            });
        }
    }, [height]);

    useEffect(() => {
        setZIndex(options.zIndex);
    }, [options.zIndex]);

    useEffect(() => {
        if(options.position) {
            setLayerPosition(options.position);
        }
    }, [options.position]);

    useEffect(() => {
        if(options.scrollToBottom) {
            scrollContainer.current?.scrollTo({ 
                behavior: 'smooth',
                top: scrollContainer.current.scrollHeight
            });
        }
    }, [children]);

    useEffect(() => {
        if(options.sizing) {
            setSize(props => ({
                ...props,
                type: Utils.isMobile() === true ? 'fullscreen' : (options.sizing || 'medium')
            }));
        }
    }, [options.sizing]);

    useEffect(() => {
        if(typeof(options.onScrollToBottom) === 'function') {
            scrollContainer.current?.addEventListener('scroll', onScrollPositionChange);
            return () => {
                scrollContainer.current?.removeEventListener('scroll', onScrollPositionChange);
            }
        }
    }, [options.onScrollToBottom]);

    useEffect(() => {
        onLayerStateChange(layerState);
    }, [layerState]);

    /*
    useEffect(() => {
        onAnimateComponentsIntoView();
    }, [options.index, options.totalLayersCount]);
    */

    useEffect(() => {

        // prevent moving forward if a ref has not yet posted
        if(!ref.current) {
            return
        }

        // animate components into view
        onAnimateComponentsIntoView();

        // update height using layer ref
        setHeight(ref.current.clientHeight);

        // add listeners for layer action changes and browser window changes
        window.addEventListener('onLayerAction', onLayerAction);
        window.addEventListener('resize', onWindowSizeChange);
        return () => {
            window.removeEventListener('onLayerAction', onLayerAction);
            window.removeEventListener('resize', onWindowSizeChange);
        }
    }, [ref.current]);

    useEffect(() => {
        if(typeof(options.onMount) === 'function') {
            let { position } = options.onMount({
                options: options,
                getWidthForSizing: getWidthForSizing
            });
            if(position) {
                setLayerPosition(position);
            }
        }
    }, [options.onMount]);

    useImperativeHandle(forwardedRef, () => ({
        scrollToTop: () => {
            if(scrollContainer.current) {
                scrollContainer.current.scrollTop = 0;
            }
        }
    }));

    return (
        <Draggable
        cancel={'.not-draggable'}
        defaultPosition={getLayerPosition()}
        key={id}
        onDrag={onDragMoved}
        onStart={onDragStarted}
        onStop={onDragEnded}
        position={getLayerPosition()}>
            <div
            id={id}
            style={{
                minWidth: getLayerWidth(),
                position: 'fixed',
                width: getLayerWidth(),
                zIndex: zIndex
            }}>
                <animated.div
                onClick={onLayerPress}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                style={{
                    backgroundColor: Appearance.colors.layerBackground(),
                    height: 'auto',
                    minWidth: getLayerWidth(),
                    overflow: 'hidden',
                    position: 'fixed',
                    width: getLayerWidth(),
                    zIndex: zIndex,
                    ...animations,
                    ...size.type !== 'fullscreen' && {
                        border: `3px solid ${window.theme === 'dark' ? 'rgba(25,25,25,1)' : 'white'}`,
                        borderRadius: 10,
                        boxShadow: '0px 0px 50px rgba(0,0,0,0.2)'
                    }
                }}>
                    <ResizeObserver onResize={onUpdateHeight}/>
                    <div
                    ref={ref}
                    style={{
                        flexGrow: 1,
                        padding: 0,
                        margin: 0
                    }}>
                        {getFilterOptions()}
                        <div
                        className={'not-draggable'}
                        style={{
                            position: 'relative'
                        }}>
                            <div ref={stickyTop}>
                                {options.sticky?.top}
                                {getWarnings()}
                            </div>
                            <div
                            ref={scrollContainer}
                            style={{
                                height: size.type === 'fullscreen' ? getLayerHeight() : '100%',
                                maxHeight: getLayerHeight(),
                                overflowX: 'hidden',
                                overflowY: 'scroll',
                                padding: options.removePadding ? 0 : 16
                            }}>
                                {getSupportComponents()}
                                {getChildren()}
                            </div>
                            <div ref={stickyBottom}>
                                {options.sticky?.bottom}
                            </div>
                            {options?.loading === true && (
                                <div style={{
                                    bottom: buttonContainer.current?.clientHeight || 0,
                                    borderRadius: 2,
                                    height: 2,
                                    left: 0,
                                    overflow: 'hidden',
                                    position: 'absolute',
                                    right: 0
                                }}>
                                    <ProgressBar/>
                                </div>
                            )}
                            {typeof(onCloseButtonClick) === 'function' && (
                                <img 
                                className={'text-button'}
                                onClick={onCloseButtonClick}
                                src={window.theme === 'dark' ? 'images/close-button-dark.png' : 'images/close-button-light.png'}
                                style={{
                                    height: 20,
                                    objectFit: 'contain',
                                    position: 'absolute',
                                    right: 16,
                                    top: 16,
                                    width: 20
                                }} />
                            )}
                            {getButtons()}
                        </div>
                    </div>
                </animated.div>
            </div>
        </Draggable>
    )
});

export const LayerItem = React.forwardRef((props, ref) => {

    const { badge, children, childrenStyle, collapsed, headerStyle, lastItem, leftContent, onVisibilityChange, spacingMultiplier = 1, subTitle, subTitleStyle, title, titleStyle, useUpperCaseTitle = true, useStyle = false, required, rightContent, style } = props;

    const [_collapsed, _setCollapsed] = useState(collapsed);
    const [animations, setAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 30 },
        opacity: collapsed ? 0 : 1,
        maxHeight: collapsed ? 0 : 1500,
    }));

    const onCollapseClick = () => {
        if(isCollapseEnabled() === false) {
            return;
        }
        let next = !_collapsed;
        _setCollapsed(next);
        if(typeof(onVisibilityChange) === 'function') {
            onVisibilityChange(next);
        }
    }

    const isCollapseEnabled = () => {
        return typeof(_collapsed) === 'boolean' ? true : false;
    }

    const getAnimationsStyles = () => {
        if(isCollapseEnabled()) {
            return {
                overflowX: 'hidden',
                overflowY: 'scroll',
                ...animations
            };
        }
        return null;
    }

    const getCollapseStyles = () => {
        if(!isCollapseEnabled() || _collapsed === false) {
            return {
                marginBottom: lastItem ? 0 : getSpacing()
            };
        }
        return {
            paddingBottom: 2,
            marginBottom: lastItem === true ? 0 : (_collapsed ? 10 : getSpacing()),
            borderBottom: `1px solid ${Appearance.colors.divider()}`
        }
    }

    const getSpacing = () => {
        return (spacingMultiplier || 1) * LayerItemSpacing;
    }

    useEffect(() => {
        setAnimations({
            opacity: _collapsed ? 0 : 1,
            maxHeight: _collapsed ? 0 : 1500
        });
    }, [_collapsed]);

    useEffect(() => {
        _setCollapsed(collapsed);
    }, [collapsed]);

    return (
        <div 
        ref={ref}
        style={{
            width: '100%',
            ...getCollapseStyles(),
            ...style
        }}>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                marginBottom: typeof(title) === 'string' ? 8 : 0,
                textAlign: 'left',
                width: '100%'
            }}>
                {required && (
                    <div style={{
                        backgroundColor: Appearance.colors.red,
                        borderRadius: 4,
                        height: 8,
                        overflow: 'hidden',
                        marginRight: 8,
                        minHeight: 8,
                        minWidth: 8,
                        width: 8
                    }} />
                )}
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    flexGrow: 1,
                    jusitfyContent: 'space-between',
                    minWidth: 0,
                    ...headerStyle
                }}>
                    {leftContent}
                    <div 
                    className={isCollapseEnabled() ? 'text-button' : ''}
                    onClick={onCollapseClick}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.layerItemTitle(),
                            fontFamily: 'Zen Dots',
                            lineHeight: 1.5,
                            paddingRight: 12,
                            whiteSpace: 'normal',
                            ...titleStyle
                        }}>{useUpperCaseTitle === false ? title : title?.toUpperCase()}</span>
                        {typeof(subTitle) === 'string' && (
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                color: Appearance.colors.subText(),
                                display: 'block',
                                lineHeight: 1.25,
                                ...subTitleStyle
                            }}>{subTitle}</span>
                        )}
                    </div>
                    {typeof(badge) === 'object' && (
                        <AltBadge
                        content={badge}
                        style={{
                            marginLeft: 8,
                            marginRight: 0,
                            top: 0
                        }} />
                    )}
                </div>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row'
                }}>
                    {rightContent}
                    {isCollapseEnabled() && (
                        <CollapseArrow
                        collapsed={_collapsed}
                        onClick={onCollapseClick} />
                    )}
                </div>
            </div>
            <animated.div style={{
                width: '100%',
                ...useStyle && {
                    ...Appearance.styles.unstyledPanel(),
                    padding: 16,
                    paddingTop: 12
                },
                ...childrenStyle,
                ...getAnimationsStyles()
            }}>
                {children}
            </animated.div>
        </div>
    )
});

export const CollapseArrow = ({ collapsed, color, onClick, style }) => {

    const [_collapsed, _setCollapsed] = useState(collapsed);
    const [animations, setAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        transform: `rotate(${collapsed === true ? '0deg' : '180deg'})`
    }));

    const onCollapseClick = evt => {
        let nextCollapse = !_collapsed;
        if(typeof(onClick) === 'function') {
            onClick(nextCollapse, evt);
        }
        _setCollapsed(nextCollapse);
    }

    const getImage = () => {
        switch(color) {
            case Appearance.colors.primary():
            return `images/down-arrow-blue-small.png`;

            case 'white':
            return `images/collapse-arrow-dark.png`;

            default:
            return window.theme === 'dark' ? `images/collapse-arrow-dark.png` : `images/collapse-arrow-light.png`;
        }
    }

    useEffect(() => {
        _setCollapsed(collapsed);
    }, [collapsed]);

    useEffect(() => {
        setAnimations({ transform: `rotate(${_collapsed === true ? '0deg' : '180deg'})` });
    }, [_collapsed]);

    return (
        <animated.img
        className={'text-button'}
        onClick={onCollapseClick}
        src={getImage()}
        style={{
            height: 15,
            objectFit: 'contain',
            width: 15,
            ...style,
            ...animations
        }} />
    )
}

export default Layer;
